import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const RULE_SEARCH_RESOURCE = "rule" as const;
export const RULE_SEARCH_OPERATION = "search" as const;
export const RULE_SEARCH_API_PATH = `/v1/${RULE_SEARCH_RESOURCE}/${RULE_SEARCH_OPERATION}` as const;

export type RuleSearchParameters = paths[typeof RULE_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type RuleSearchBody = paths[typeof RULE_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type RuleSearchResponse =
  paths[typeof RULE_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useRuleSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<RuleSearchParameters, RuleSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<RuleSearchResponse, Error, RuleSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<RuleSearchParameters, RuleSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      RULE_SEARCH_RESOURCE,
      RULE_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<RuleSearchResponse>(
        RULE_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
